import { useTranslation } from 'react-i18next';
import '../../../styles/pages/aiOptimizations/aiOptimizationsAutomated.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import IconSelectInput from '../../../ui/inputs/IconSelectInput';
import { useState, useEffect } from 'react';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import { cursorSVG } from '../../../lib/allCustomSVGs';
import get_automated_recommendation_settings_api_call from '../../../lib/api/ai-tracker/get_automated_recommendation_settings_api_call';
import update_automated_recommendation_settings_api_call from '../../../lib/api/ai-tracker/update_automated_recommendation_settings_api_call';
import { AutomatedRecommendationSettings } from '../../../interfaces/AIOptimizations';

type Props = {
  elementName: string | null;
  recommendationId?: string;
  toggleSettingsPopupModal: (isOpen: boolean) => void;
};

const AIOptimizationsAutomatedSettings = ({
  elementName = '',
  recommendationId,
  toggleSettingsPopupModal,
}: Props) => {
  const { t } = useTranslation();
  const [confidenceLevel, setConfidenceLevel] = useState('0');
  const [minimumSampleSize, setMinimumSampleSize] = useState('0');
  const [trafficSplit, setTrafficSplit] = useState('0');
  const [duration, setDuration] = useState('0');
  const [conversionGoal, setConversionGoal] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      if (!recommendationId) {
        setInitialLoading(false);
        return;
      }

      try {
        const settings = await get_automated_recommendation_settings_api_call(
          recommendationId
        );
        if (settings) {
          setTrafficSplit(settings.traffic_split?.toString() || '0');
          setDuration(settings.duration?.toString() || '0');
          setConfidenceLevel(settings.confidence_level?.toString() || '0');
          setMinimumSampleSize(settings.minimum_sample_size?.toString() || '0');
          setConversionGoal(settings.conversion_goal || '');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadSettings();
  }, [recommendationId]);

  const handleSave = async () => {
    if (!recommendationId) {
      return;
    }

    setLoading(true);
    try {
      const settings: AutomatedRecommendationSettings = {
        traffic_split: parseInt(trafficSplit),
        duration: parseInt(duration),
        confidence_level: parseInt(confidenceLevel),
        minimum_sample_size: parseInt(minimumSampleSize),
        conversion_goal: conversionGoal || undefined,
      };

      const success = await update_automated_recommendation_settings_api_call(
        recommendationId,
        settings
      );
      if (success) {
        toggleSettingsPopupModal(false);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className='aiOptimizationsAutomatedSettings'>
        <div className='aiOptimizationsAutomatedSettings__title'>
          <CustomSVGs svg={cursorSVG} />
          <h3>{`${t('Loading')}...`}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className='aiOptimizationsAutomatedSettings'>
      <div className='aiOptimizationsAutomatedSettings__title'>
        <CustomSVGs svg={cursorSVG} />
        <h3>{`${t('Configure')} ${elementName}`}</h3>
      </div>

      <div className='aiOptimizationsAutomatedSettings__inputs'>
        <div className='aiOptimizationsAutomatedSettings__inputs--content'>
          <div className='aiOptimizationsAutomatedSettings__inputs--content-1'>
            <AINormalInput
              changeListeners={[(e) => setTrafficSplit(e.target.value)]}
              id='TrafficSplit-input'
              value={trafficSplit}
              placeholder={t('TrafficSplit(%)')}
              required={true}
              type='number'
              min={0}
              max={100}
            />
          </div>
          <div className='aiOptimizationsAutomatedSettings__inputs--content-2'>
            <AINormalInput
              changeListeners={[
                (e) => {
                  const value = Number(e.target.value) as number;
                  if (value < 1) {
                    setDuration('1');
                  } else {
                    setDuration(value.toString());
                  }
                },
              ]}
              id='Duration-input'
              value={duration}
              defaultValue={duration}
              placeholder={t('Duration(days)')}
              required={true}
              type='number'
              min={1}
            />
          </div>
        </div>
        <div className='aiOptimizationsAutomatedSettings__inputs--content'>
          <div className='aiOptimizationsAutomatedSettings__inputs--content-1'>
            <IconSelectInput
              filterOptions={Array.from({ length: 21 }, (_, i) => ({
                text: `${(i + 80).toString()}%`,
                value: (i + 80).toString(),
                icon: null,
              }))}
              title={t('ConfidenceLevel(%)')}
              value={confidenceLevel}
              onFilterChange={(val) => setConfidenceLevel(val)}
              marginTop='1.5rem'
              scrollWhenOpen={true} // ✅ Enable scroll into view when dropdown opens
            />
          </div>
          <div className='aiOptimizationsAutomatedSettings__inputs--content-2'>
            <AINormalInput
              changeListeners={[(e) => setMinimumSampleSize(e.target.value)]}
              id='minimum-sample-size-input'
              value={minimumSampleSize}
              placeholder={t('MinimumSampleSize')}
              required={true}
              type='number'
              min={0}
            />
          </div>
        </div>

        <div className='aiOptimizationsAutomatedSettings__inputs--contentLong'>
          <IconSelectInput
            title={t('ConversionGoal')}
            filterOptions={[
              { text: t('AddToCart'), value: 'addToCart', icon: null },
              { text: t('Purchase'), value: 'purchase', icon: null },
              { text: t('ViewProduct'), value: 'viewProduct', icon: null },
              { text: t('SignUp'), value: 'signUp', icon: null },
              { text: t('Click'), value: 'click', icon: null },
            ]}
            value={conversionGoal}
            onFilterChange={(val) => setConversionGoal(val)}
            scrollWhenOpen={true} // ✅ Enable scroll into view when dropdown opens
          />
        </div>
      </div>

      <div className='aiOptimizationsAutomatedSettings__buttons'>
        <MainSquareButton
          value={t('Cancel')}
          type='button'
          buttonType='white'
          disabled={loading}
          onClick={() => toggleSettingsPopupModal(false)}
        />
        <MainSquareButton
          value={loading ? t('Saving') : t('SaveConfiguration')}
          type='button'
          disabled={loading}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default AIOptimizationsAutomatedSettings;
